import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { ssgData } from "@/lib/ssg";
import useSEO from "@/hooks/useSEO";

const FALLBACK = "https://images.unsplash.com/photo-1714976694810-85add1a29c96?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxlbXBhdGhldGljJTIwZG9jdG9yJTIwdGhlcmFwaXN0JTIwcGF0aWVudHxlbnwwfHx8fDE3Nzg3NDM1MDV8MA&ixlib=rb-4.1.0&q=85";

export default function Blog() {
  useSEO({
    title: "Blog — Psychiatry, Deaddiction & Neuromodulation",
    description: "Evidence-based articles from Manas Neuropsychiatry Hospital on depression, anxiety, addiction, rTMS, child psychiatry, sleep, sexology and more. Written by Dr. Kamlesh & Dr. Dharmin Shah's team.",
  });
  const [posts, setPosts] = useState(() => ssgData("blogPosts") || []);
  const [loading, setLoading] = useState(!ssgData("blogPosts"));
  const [category, setCategory] = useState("All");

  useEffect(() => {
    api.get("/blog/posts").then(({ data }) => {
      setPosts(data);
      setLoading(false);
    });
  }, []);

  const categories = ["All", ...Array.from(new Set(posts.map((p) => p.category)))];
  const filtered = category === "All" ? posts : posts.filter((p) => p.category === category);

  return (
    <div data-testid="blog-page">
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-12">
        <p className="eyebrow text-[#A39171] mb-6">Journal</p>
        <h1 className="font-display text-5xl md:text-7xl text-[#2D2623] leading-[0.95] tracking-tight max-w-4xl">
          Reflections on the <span className="italic text-[#E07A5F]">human mind</span>.
        </h1>
        <p className="mt-8 text-xl text-[#5C524E] max-w-2xl leading-relaxed">
          Field notes, clinical observations and the occasional unguarded thought — from our team
          to anyone who is listening.
        </p>
      </section>

      {/* Filters */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 mb-10">
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`blog-filter-${c}`}
              onClick={() => setCategory(c)}
              className={`rounded-full px-5 h-10 text-sm font-medium transition-all ${
                category === c
                  ? "bg-[#2D2623] text-[#FDFBF7]"
                  : "bg-[#F4EFE6] text-[#2D2623] hover:bg-[#E07A5F]/15"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-28">
        {loading ? (
          <p className="text-[#A39171]">Loading articles…</p>
        ) : filtered.length === 0 ? (
          <p className="text-[#A39171]">No articles in this category yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p, i) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${i}`}
                className="group bg-[#F4EFE6] rounded-3xl overflow-hidden border border-[#A39171]/10 hover:shadow-[0_24px_60px_rgba(163,145,113,0.15)] transition-all duration-500 hover:-translate-y-1"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={p.cover_image || FALLBACK} alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-7">
                  <div className="flex items-center justify-between mb-3">
                    <p className="eyebrow text-[#A39171]">{p.category}</p>
                    <p className="text-xs text-[#A39171]">{new Date(p.created_at).toLocaleDateString("en-IN", { month: "short", day: "numeric", year: "numeric" })}</p>
                  </div>
                  <h3 className="font-display text-2xl text-[#2D2623] leading-tight tracking-tight group-hover:text-[#E07A5F] transition-colors">{p.title}</h3>
                  <p className="text-[#5C524E] mt-3 line-clamp-2">{p.excerpt}</p>
                  <span className="mt-5 inline-flex items-center gap-1 text-sm text-[#2D2623] font-medium">
                    Read article <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
